import { WorkspaceSubscriptionPlan } from '@airimpact/types/modelEnums'
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Typography, Button } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { IError } from './payment-service/CheckoutScreen'
import { useProjectsContext } from '../hooks/useProjects'
import { useSnackbarContext } from '../hooks/useSnackBar'
import { PlanData, PlanDescription, PlanDetails, useTierSelectionContext } from '../hooks/useTierSelection'
import { useWorkspaceContext } from '../hooks/useWorkspace'
import { ReactComponent as IconCheck } from '../icons/Check icon.svg'

interface ITierComponent extends PlanDetails {
  selectedInterval: string
  planObject: PlanData
  onTierClick: () => void
}

export function TierComponent({ plan, description, selectedInterval, onTierClick, planObject }: ITierComponent) {
  const [isFreeSubscriptionLoading, setIsFreeSubscriptionLoading] = useState<boolean>(false)

  const navigate = useNavigate()

  const { selectedNodeId, updateSelectedWorkspace, selectedWorkspaceDetails } = useWorkspaceContext()
  const { openSnackBar } = useSnackbarContext()
  const { subscribe } = useTierSelectionContext()
  const { fetchProjects } = useProjectsContext()

  const priceDetails = planObject.intervals.find((intervalDetails) => intervalDetails.interval === selectedInterval)
  const priceFormatted = priceDetails ? priceDetails.unit_amount_formatted.replace(/,/g, '') : '0'

  const indexOfDataStorage = description.findIndex((item) => item.text === 'Data storage')
  const mainPlanDescriptionList = indexOfDataStorage !== -1 ? description.slice(0, indexOfDataStorage) : description
  const additionalPlanDescriptionList = indexOfDataStorage !== -1 ? description.slice(indexOfDataStorage) : []

  const isEligibleForTrial = selectedWorkspaceDetails.subscription?.isEligibleForTrial

  const onSubscribeToFreePlan = async () => {
    try {
      setIsFreeSubscriptionLoading(true)
      const payload = {
        interval: selectedInterval || '',
        items: [
          {
            product_id: planObject._id || '',
            quantity: 1,
          },
        ],
      }
      const { clientSecret } = await subscribe(payload)

      if (!clientSecret) {
        await updateSelectedWorkspace()
        await fetchProjects()
        openSnackBar({
          message: 'Subscription created!',
          type: 'success',
        })

        navigate(`/${selectedNodeId}`)
      }
      setIsFreeSubscriptionLoading(false)
    } catch (e) {
      setIsFreeSubscriptionLoading(true)
      const errorMessage = (e as IError)?.response?.data?.message || 'Something went wrong!'
      openSnackBar({
        message: errorMessage,
        type: 'error',
      })
      setIsFreeSubscriptionLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        width: '302px',
        padding: 2.5,
        paddingBottom: 5,
        borderRadius: '12px',
        backgroundColor: 'white',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
        <Typography variant="h4" color="primary.800" mr={1}>
          {plan}
        </Typography>

        {plan === WorkspaceSubscriptionPlan.Premium && '✨'}
      </Box>

      <Typography variant="body1" color="grey.500" mb={4.25}>
        {planObject.description || ''}
      </Typography>

      <Box sx={{ display: 'flex', marginBottom: 4.25, alignItems: 'baseline' }}>
        <Typography variant="display3" color="primary.800" mr={0.75}>
          ${selectedInterval === 'year' ? Number(priceFormatted) / 12 : priceFormatted.split('.')[0]}
        </Typography>

        <Typography variant="subtitle1" color="grey.500">
          USD / user / month
        </Typography>
      </Box>

      <Box sx={{ marginBottom: 4 }}>
        {Number(priceFormatted) === 0 && (
          <LoadingButton
            fullWidth
            variant="outlined"
            loading={isFreeSubscriptionLoading}
            onClick={onSubscribeToFreePlan}
            sx={{ boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)' }}
          >
            {isEligibleForTrial ? 'Get started for free' : 'Activate subscription'}
          </LoadingButton>
        )}

        {Number(priceFormatted) > 0 && (
          <Button variant="contained" fullWidth onClick={onTierClick}>
            {isEligibleForTrial ? 'Try free for 30 days' : 'Activate subscription'}
          </Button>
        )}
      </Box>

      <Box sx={{ marginBottom: 4 }}>
        <Box>
          {mainPlanDescriptionList?.map((option: PlanDescription) => (
            <Box key={option.text} sx={{ display: 'flex', marginBottom: 1.5 }}>
              <IconCheck style={{ flexShrink: 0, width: '18px', height: '18px' }} />

              <Box sx={{ marginLeft: 1.5 }}>
                <Tooltip
                  title={
                    option.hint && (
                      <Typography variant="caption" color="grey.700">
                        {option.hint}
                      </Typography>
                    )
                  }
                  placement="bottom"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        boxSizing: 'border-box',
                        width: '268px',
                        paddingY: 1,
                        paddingX: 1.5,
                        backgroundColor: 'white',
                        borderRadius: '5px',
                        boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.12), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
                      },
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    color="grey.700"
                    sx={{
                      cursor: option.hint ? 'pointer' : 'initial',
                      '& span.additional-plan-details': {
                        fontSize: '14px',
                        color: 'grey.450',
                      },
                    }}
                  >
                    <span style={{ textDecoration: option.hint ? 'underline dotted' : '' }}>{option.text}</span>
                    <span className="additional-plan-details">&nbsp;{option.feature_limit}</span>
                    {option.coming_soon && <span className="additional-plan-details">(coming soon)</span>}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
        {additionalPlanDescriptionList.length > 0 && (
          <Box sx={{ marginTop: 2.5, paddingTop: 2.5, borderTop: '1px solid', borderColor: 'grey.200' }}>
            {additionalPlanDescriptionList?.map((option: PlanDescription) => (
              <Box key={option.text} sx={{ display: 'flex', marginBottom: 1.5 }}>
                <IconCheck style={{ flexShrink: 0, width: '18px', height: '18px' }} />

                <Box sx={{ marginLeft: 1.5 }}>
                  <Typography
                    variant="subtitle1"
                    color="grey.700"
                    sx={{
                      cursor: option.hint ? 'pointer' : 'initial',
                      '& span.additional-plan-details': {
                        fontSize: '14px',
                        color: 'grey.450',
                      },
                    }}
                  >
                    {option.text}
                    <span className="additional-plan-details">&nbsp;{option.feature_limit}</span>
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {plan === WorkspaceSubscriptionPlan.Advanced && (
        <Box sx={{ marginBottom: 4, padding: 1.5, borderRadius: '6px', backgroundColor: 'grey.50' }}>
          <Typography
            variant="subtitle1"
            color="grey.700"
            mb={1.5}
            sx={{
              '& span': {
                fontSize: '14px',
                color: 'grey.450',
              },
            }}
          >
            Access Monitoring integrations&nbsp;
            <span>custom price (coming soon)</span>
          </Typography>

          <Typography
            variant="subtitle1"
            color="grey.700"
            sx={{
              '& span': {
                fontSize: '14px',
                color: 'grey.450',
              },
            }}
          >
            Verified assets&nbsp;
            <span>(coming soon) $0.3/asset</span>
          </Typography>
        </Box>
      )}

      {plan === WorkspaceSubscriptionPlan.Premium && (
        <Box sx={{ marginBottom: 4, padding: 1.5, borderRadius: '6px', backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle1" color="grey.700" mb={0.5} sx={{ fontWeight: 500 }}>
            Partner sales connections
          </Typography>

          <Typography variant="body1" color="grey.500">
            Apply for premium project status to get access to sell forwards and credits with our sales partners and get
            featured on AirImpact
          </Typography>
        </Box>
      )}
    </Box>
  )
}
